'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/Button';
import OptimizedImage from '@/components/ui/OptimizedImage';

export default function TarotCardsClient({ cards }) {
  const locale = useLocale();
  const t = useTranslations('tarot.index');
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState('');

  const groupedCards = useMemo(() => {
    const groups = {
      major_arcana: [],
      wands: [],
      cups: [],
      swords: [],
      pentacles: [],
    };

    cards.forEach((card) => {
      if (groups[card.suit]) {
        groups[card.suit].push(card);
      }
    });

    return groups;
  }, [cards]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) {
      return groupedCards;
    }

    const lowercasedFilter = searchTerm.toLowerCase();
    const filtered = {};

    for (const group in groupedCards) {
      filtered[group] = groupedCards[group].filter((card) =>
        card.name.toLowerCase().includes(lowercasedFilter)
      );
    }

    return filtered;
  }, [searchTerm, groupedCards]);

  const sortGroupCards = (cardsInGroup) => {
    const rankOrder = ['ace', 'asso', 'as', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'page', 'paje', 'fante', 'knight', 'caballero', 'cavaliere', 'queen', 'reina', 'regina', 'king', 'rey', 're'];

    return [...cardsInGroup].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;

      if (a.suit === 'major_arcana' && b.suit === 'major_arcana') {
        return orderA - orderB;
      }

      const valueRank = (value) => {
        if (typeof value === 'number') return value;
        if (!value) return Number.MAX_SAFE_INTEGER;
        const normalized = value.toString().toLowerCase();
        const index = rankOrder.indexOf(normalized);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      };

      const rankA = valueRank(a.value);
      const rankB = valueRank(b.value);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      return orderA - orderB;
    });
  };

  const renderCardGroup = (title, groupName) => {
    const cardsInGroup = filteredGroups[groupName];
    if (cardsInGroup.length === 0) return null;

    const sortedCards = sortGroupCards(cardsInGroup);

    return (
      <div key={groupName} className="mb-12">
        <h2 className="text-purple-400 text-sm font-semibold tracking-wider uppercase mb-2 text-center" style={{ fontFamily: "var(--font-marcellus)" }}>{title}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {sortedCards.map((card) => (
            <Link key={card.urlSlug} href={`/${locale}/tarot-cards/${card.urlSlug}`} passHref>
              <div className="flex flex-col items-center p-2 rounded-lg hover:bg-[#A45DE8] transition-colors duration-300 text-center">
                <OptimizedImage
                  src={card.cardArtUrl}
                  alt={`${card.name} Tarot Card`}
                  width={150}
                  height={250}
                  className="w-full h-auto rounded-lg mb-2"
                  quality={75} // Explicitly setting quality to 75
                  sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, (max-width: 1280px) 20vw, 16.66vw"
                />
                <span className="font-inter text-sm">{card.name}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#050316] text-[#FBFBFE] min-h-screen">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-5xl font-marcellus font-bold mb-4" style={{ fontFamily: "var(--font-marcellus)" }}>{t('title')}</h1>
          <p className="text-lg text-[#FBFBFE] font-inter mb-6">
            {t('description')}
          </p>
          <div className="mb-12">
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-md px-4 py-2 rounded-lg bg-[#1E1A3C] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#A45DE8]"
            />
          </div>
          <Link href={`/${locale}/#hero`} passHref>
            <Button variant="primary">
              {t('cta')}
            </Button>
          </Link>
          <div className="text-sm text-gray-300 mt-4 flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <span className="text-[#D4B6FF] mr-1">✧</span>
              <span>{t('badge.free')}</span>
            </div>
            <div className="flex items-center">
              <span className="text-[#D4B6FF] mr-1">✧</span>
              <span>{t('badge.private')}</span>
            </div>
          </div>
        </div>

        <div className="text-center mb-12">
            <h2 className="text-2xl font-marcellus" style={{ fontFamily: "var(--font-marcellus)" }}>{t('listTitle')}</h2>
        </div>

        <div>
            {renderCardGroup(t('groups.majorArcana'), 'major_arcana')}
            {renderCardGroup(t('groups.wands'), 'wands')}
            {renderCardGroup(t('groups.cups'), 'cups')}
            {renderCardGroup(t('groups.swords'), 'swords')}
            {renderCardGroup(t('groups.pentacles'), 'pentacles')}
        </div>
      </div>
    </div>
  );
} 